// Share one in-flight (or just-finished) GET between callers that ask for the same thing.
//
// The Control Center mounts several cards and panes that each load the observatory list on
// their own and re-poll it every 30s. Every one of those was a full GET /v1/observatories,
// and the server answers them one at a time, so a dashboard load queued five-plus identical
// requests and the last ones ran past the 10s client timeout. Coalescing them turns that into
// one request per key per `ttlMs`.
//
// Each caller gets its own deep copy: a card that sorts or annotates its rows in place must
// not change what another card sees.

const clone = (v) => (typeof structuredClone === 'function' ? structuredClone(v) : JSON.parse(JSON.stringify(v)));

export function createCoalescer({ ttlMs = 10000, now = () => Date.now() } = {}) {
  const entries = new Map(); // key -> { promise, settledAt }

  return {
    /** Run `fn` for `key`, or reuse the pending/recent result of an earlier call for the same key. */
    get(key, fn) {
      const hit = entries.get(key);
      if (hit && (hit.settledAt == null || now() - hit.settledAt < ttlMs)) {
        return hit.promise.then(clone);
      }
      const entry = { settledAt: null };
      entry.promise = Promise.resolve()
        .then(fn)
        .then(
          (value) => { entry.settledAt = now(); return value; },
          (err) => { if (entries.get(key) === entry) entries.delete(key); throw err; }, // never cache a failure
        );
      entries.set(key, entry);
      return entry.promise.then(clone);
    },
    /** Forget everything (after a write that changes what the cached reads would return). */
    clear() { entries.clear(); },
    get size() { return entries.size; },
  };
}

/** Stable key for a params object: same keys/values in any order give the same key. */
export const paramsKey = (params = {}) => JSON.stringify(
  Object.keys(params).sort().filter((k) => params[k] !== undefined).map((k) => [k, params[k]]),
);
